import { LoadingOverlay } from "@mantine/core";
import { safeApiCall } from "api/common.service";
import { getAgedTrialBalanceReportByDebtor } from "api/report.service";
import { printPage } from "common/utils";
import React, { useEffect, useState } from "react";
import { AgedTrialBalance, AgedTrialBalances } from "types/model.types";
import AdminInvoiceView from "./components/AdminInvoiceView";
import AdminStatementView from "./components/AdminStatementView";

interface AdminStatementAndInvoicesPreviewProps {
  id: number;
  includeInvoices?: boolean;
}

const AdminStatementAndInvoicesPreview: React.FC<
  AdminStatementAndInvoicesPreviewProps
> = ({ id, includeInvoices }) => {
  const [data, setData] = useState<AgedTrialBalances | null>(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return;

    let mounted = true;
    setBusy(true);

    const fetchReport = async () => {
      try {
        const results = await safeApiCall(
          getAgedTrialBalanceReportByDebtor(id, includeInvoices),
        );
        if (mounted) {
          setData(results ?? null);
        }
      } finally {
        setBusy(false);
      }
    };

    fetchReport();

    // Placeholder for any future data fetching or setup logic
    return () => {
      mounted = false;
    };
  }, [id, includeInvoices]);

  const handlePrint = () => {
    //setExporting(true);

    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "none";
    });

    const preview = document.getElementById("preview-content");
    if (!preview) {
      return;
    }
    printPage(preview.innerHTML);

    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "";
    });
  };

  if (busy) {
    return (
      <>
        <div className="attention-banner">
          <div className="attention-title">
            ⚠️ Statement and Invoice Preview Warning
          </div>

          <div className="attention-body">
            If you are seeing this message, it means the system is still
            preparing the Statement and Invoice preview for you. This process
            can take up to a minute or more, especially if this is a parent
            account with many child accounts or a large number of transactions.
          </div>
        </div>
        <LoadingOverlay visible={true} opacity={1} />
      </>
    );
  }

  return (
    <div>
      {data?.map((agedTrialBalance: AgedTrialBalance, index: number) => {
        const isFirst = index === 0;

        return (
          <div key={`debtor-${index}`}>
            {/* Page break before every debtor except the first */}
            {!isFirst && (
              <div
                style={{
                  pageBreakBefore: "always",
                }}
              />
            )}

            <AdminStatementView data={agedTrialBalance} />

            {/* Render invoices */}
            {includeInvoices &&
              agedTrialBalance.invoices?.map((invoice, index2) => (
                <div
                  key={`inv-${index}-${index2}`}
                  style={{
                    pageBreakBefore: "always",
                  }}
                >
                  <AdminInvoiceView invoice={invoice} />
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
};

export default AdminStatementAndInvoicesPreview;
